<template>
  <div class="course-details">
    <v-overlay :value="loadingCurrentCourse" z-index="4">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
    <course-details-tabs
      v-if="(loadingCurrentCourse && !currentCourse) || currentCourse"
      @change-tab="changeActiveListTab"
    >
      <template v-slot:settings>
        <course-form
          :course="currentCourse"
          :readonly="!isMyCourse"
          :hide-non-editable="
            !isMyCourse || (isMyCourse && isCompanyUser && !isAdvisor)
          "
          :key="`course-form-${currentCourse ? currentCourse.id : 'none'}`"
          @submit="submitForm"
          @open-assign="openParticipantSelectorDialog"
          @open-delete-course="showConfirmDeleteCourseDialog"
          @refresh-course-media="refreshCourseMedia"
        />
      </template>
      <template v-slot:steps>
        <v-overlay :value="loadingCurrentCourseSteps" z-index="4">
          <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>
        <course-steps-list
          v-if="currentCourseSteps && currentCourseSteps.length"
          :course-steps="currentCourseSteps"
          :readonly="!isMyCourse"
          @step-dragged="stepDragged"
          @open-edit="openEdit"
          @open-delete="openDelete"
          @refresh-course-steps-media="refreshCourseStepsMedia"
        />
        <div
          class="d-flex flex-column align-center justify-center text-center pa-6 ma-6"
          v-else
        >
          <v-btn
            class="rounded-pill"
            color="accent"
            @click="showCourseStepFormDialog(null)"
            v-if="isMyCourse"
            ><v-icon class="mr-2">bhi-plus</v-icon>Create first step</v-btn
          >
          <h4 class="mb-4" v-else>There are no steps.</h4>
        </div>
      </template>
    </course-details-tabs>
    <div
      class="d-flex flex-column align-center justify-center text-center pa-6 ma-6"
      v-else
    >
      <h4 class="mb-4">Course not found.</h4>
      <v-btn
        class="rounded-pill"
        color="accent"
        @click="navigateToCreateCourse"
        v-if="isCompanyUser"
        ><v-icon class="mr-2">bhi-plus</v-icon>Create course</v-btn
      >
    </div>
    <normal-dialog
      :max-width="400"
      :title="currentCourseStep ? 'Update step' : 'Create new step'"
      cancelText="Cancel"
      :submitText="currentCourseStep ? 'Save' : 'Create'"
      :showDialog="courseStepFormDialogActive"
      :loading-buttons="loadingCurrentCourseStep"
      ref="courseStepFormDialog"
      color="accent"
      @cancel="courseStepFormDialogCancel"
      @submit="courseStepFormDialogSubmit"
    >
      <course-step-form
        :course-step="
          currentCourseStep === null ? undefined : currentCourseStep
        "
        :key="
          `course-step-dialog-form-${
            currentCourseStep ? currentCourseStep.id || 'unknown' : 'null'
          }-${courseStepFormDialogActive ? 'on' : 'off'}`
        "
        :force-rules="stepFormForceRules"
        ref="course-step-form"
      />
    </normal-dialog>
    <confirm-dialog
      title="Are you sure you want to delete this item? 🥵"
      cancelText="No"
      submitText="Yes"
      color="accent"
      :loading-buttons="loadingCurrentCourseSteps"
      :showDialog="confirmDeleteStepDialogAlert"
      @cancel="confirmDeleteStepDialogCancel"
      @submit="confirmDeleteStepDialogSubmit"
    />
    <confirm-dialog
      title="Are you sure you want to delete this item? 🥵"
      cancelText="No"
      submitText="Yes"
      color="accent"
      :loading-buttons="loadingCurrentCourse"
      :showDialog="confirmDeleteCourseDialogAlert"
      @cancel="confirmDeleteCourseDialogCancel"
      @submit="confirmDeleteCourseDialogSubmit"
    />
    <normal-dialog
      :max-width="448"
      :showDialog.sync="participantSelectorDialogActive"
      content-class="participant-selector-dialog ma-4 ml-auto"
      title="Select participants"
      :full-height="true"
      ref="participantSelectorDialog"
      color="accent"
      @cancel="afterCloseParticipantSelectorDialog"
    >
      <v-overlay :value="loadingCurrentCourseParticipants" z-index="4">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
      <participant-selector
        :key="`participants-${participantSelectorDialogActive ? 'on' : 'off'}`"
        ref="participantSelector"
        :initial-select-all="initialSelectAll"
        :initial-select-all-and-future="initialSelectAllAndFuture"
        :initial-selection="initialSelection"
        initial-selection-prop="participantId"
        :all-enabled="true"
        :all-and-future-enabled="true"
      />
      <v-btn
        elevation="2"
        bottom
        right
        @click="applyAssignParticipants"
        color="accent"
        class="participant-selector-dialog__apply rounded-pill"
        :loading="loadingCurrentCourse"
      >
        Apply
      </v-btn>
    </normal-dialog>
    <confirm-dialog
      title="Are you sure you want to edit?"
      description="Your changes will be visible immediately for all assigned Participants."
      cancelText="No"
      submitText="Yes"
      color="accent"
      :showDialog="confirmEditLiveStepDialogAlert"
      @cancel="confirmEditLiveStepDialogCancel"
      @submit="confirmEditLiveStepDialogSubmit"
    />
    <template v-if="courseDetailsActiveTab === 'course-steps'">
      <v-btn
        elevation="2"
        fab
        bottom
        right
        v-if="isMyCourse"
        @click="showCourseStepFormDialog(null)"
        color="accent"
        class="course-details__fab rounded-pill"
      >
        <v-icon>bhi-plus</v-icon>
      </v-btn>
    </template>
  </div>
</template>

<script>
import { mapGetters, mapState, mapActions, mapMutations } from 'vuex'
import CourseDetailsTabs from '@/components/courses/CourseDetailsTabs.vue'
import CourseStepsList from '@/components/courses/CourseStepsList.vue'
import CourseForm from '@/components/courses/CourseForm.vue'
import CourseStepForm from '@/components/courses/CourseStepForm.vue'
import NormalDialog from '@/components/dialogs/NormalDialog.vue'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import ParticipantSelector from '@/components/inputs/ParticipantSelector.vue'
import { debounce } from '@/helpers/debounce'
import urlValidation from '@/helpers/urlValidation.js'

export default {
  name: 'CourseDetails',
  components: {
    CourseDetailsTabs,
    CourseForm,
    CourseStepForm,
    NormalDialog,
    ConfirmDialog,
    CourseStepsList,
    ParticipantSelector,
  },
  mixins: [],
  props: {},
  data() {
    return {
      courseStepFormDialogActive: false,
      confirmDeleteStepDialogAlert: false,
      confirmEditLiveStepDialogAlert: false,
      confirmDeleteCourseDialogAlert: false,
      participantSelectorDialogActive: false,
      drag: false,
      courseDetailsActiveTab: 'course-settings',
      initialSelectAll: false,
      initialSelectAllAndFuture: false,
      stepFormForceRules: false,
      stepToEdit: null,

      initialSelection: [],
    }
  },
  computed: {
    ...mapGetters('dictionariesStates', ['getMimeTypes']),
    ...mapState('dictionariesStates', [
      'dictionariesLoaded',
      'dictionaryPracticeUserRole',
      'dictionaryBhUsersRoleIds',
      'dictionaryFeatureFlagState',
    ]),
    ...mapState('coursesStates', [
      'currentCourse',
      'loadingCurrentCourse',
      'loadingCurrentCourseStep',
      'loadingCurrentCourseSteps',
      'currentCourseSteps',
      'currentCourseStep',
      'currentCourseAssignmentStatus',
      'currentCourseParticipants',
      'loadingCurrentCourseParticipants',
    ]),
    ...mapState('userStates', ['profileId']),
    ...mapGetters('userStates', ['getRolename', 'getToken']),
    isCompanyUser() {
      // is Company Admin, Company Advisor or Company Team
      return (
        this.dictionaryPracticeUserRole[this.getRolename] ===
          this.dictionaryPracticeUserRole.Advisor ||
        this.dictionaryPracticeUserRole[this.getRolename] ===
          this.dictionaryPracticeUserRole.Administrator ||
        this.dictionaryPracticeUserRole[this.getRolename] ===
          this.dictionaryPracticeUserRole.Team
      )
    },
    isAdvisor() {
      return (
        this.dictionaryPracticeUserRole[this.getRolename] ===
        this.dictionaryPracticeUserRole.Advisor
      )
    },
    isMyCourse() {
      return (
        String(this.profileId) ===
        String(this.currentCourse?.createdBy?.profileId)
      )
    },
  },
  beforeRouteLeave: function(to, from, next) {
    this.setBreadcrumbCurrentView('')
    this.$store.commit('coursesStates/setCurrentCourse', null)
    this.$store.commit('coursesStates/setCurrentCourseStep', null)
    this.$store.commit('coursesStates/setCurrentCourseSteps', [])
    this.$store.commit('coursesStates/setCurrentCourseParticipants', [])
    next()
  },
  mounted() {
    const courseId = this?.$route?.params?.id
    this.getCourseData(courseId)
  },
  methods: {
    ...mapMutations('appStates', [
      'setSnackbarNotification',
      'setBreadcrumbCurrentView',
    ]),
    ...mapActions('coursesStates', [
      'getCourseDefinition',
      'updateCourseDefinition',
      'deleteCourseDefinition',
      'getCourseStep',
      'getCourseSteps',
      'updateCourseStep',
      'createCourseStep',
      'deleteCourseStep',
      'moveCourseStep',
      'assignCourseDefinition',
      'getCourseDefinitionParticipants',
    ]),
    ...mapMutations('coursesStates', ['setLoadingCurrentCourse']),
    validUrl: urlValidation.validUrl,
    addHttpsProtocol: urlValidation.addHttpsProtocol,
    changeActiveListTab(tab) {
      this.courseDetailsActiveTab = tab

      const courseId = this?.$route?.params?.id
      if (!courseId) return

      if (tab === 'course-steps') {
        this.getCourseSteps({ courseId })
      } else if (tab === 'course-settings') {
        this.getCourseDefinition({ id: courseId })
      }
    },
    submitForm(data) {
      if (data.description.length > 30000) {
        this.setSnackbarNotification('Description is too long')
        return
      }

      const { successMessage, ...rest } = data
      this.updateCourseDefinition({ data: rest, successMessage })
    },
    showCourseStepFormDialog(step = null) {
      this.$store.commit('coursesStates/setCurrentCourseStep', step)
      this.courseStepFormDialogActive = true
    },
    refreshCourseMedia() {
      const courseId = this?.$route?.params?.id
      if (courseId) {
        this.getCourseDefinition({ id: courseId })
      }
    },
    refreshCourseStepsMedia() {
      const courseId = this?.$route?.params?.id
      if (courseId) {
        this.getCourseSteps({ courseId })
      }
    },
    openEditStep(step) {
      if (
        this.currentCourse.visibilityStatus !== 'DRAFT' &&
        this.currentCourse.participantsCount > 0
      ) {
        this.showConfirmEditLiveStepDialog(step)
      } else {
        this.showCourseStepFormDialog(step)
      }
    },
    showConfirmEditLiveStepDialog(step) {
      this.stepToEdit = step
      this.confirmEditLiveStepDialogAlert = true
    },
    confirmEditLiveStepDialogCancel() {
      this.confirmEditLiveStepDialogAlert = false
      this.stepToEdit = null
    },
    confirmEditLiveStepDialogSubmit() {
      const step = this.stepToEdit
      this.confirmEditLiveStepDialogAlert = false
      this.showCourseStepFormDialog(step)
      this.stepToEdit = null
    },
    courseStepFormDialogCancel() {
      this.courseStepFormDialogActive = false
      this.$store.commit('coursesStates/setCurrentCourseStep', null)
    },
    getCourseData(courseId = null) {
      debounce(
        () => {
          this.getCourseDefinition({ id: courseId })
          this.getCourseSteps({ courseId: courseId })
        },
        150,
        'getCourseData',
      )
    },
    stepFormIsValid(data = {}) {
      try {
        const isLinkValid =
          !data?.link || (data?.link && this.validUrl(data?.link))

        return (
          data?.title &&
          data?.title?.length > 0 &&
          data?.title?.length <= 255 &&
          data?.description &&
          data?.description?.length > 0 &&
          data?.description?.length <= 30000 &&
          isLinkValid
        )
      } catch (_) {
        return false
      }
    },
    async courseStepFormDialogSubmit() {
      const courseStepForm = this.$refs['course-step-form']
      const courseStepFormData = courseStepForm?.getData?.() || {}

      const { id: stepId, ...rest } = courseStepFormData

      const data = {
        courseId: this.$route.params.id,
        data: rest,
      }

      if (!this.stepFormIsValid(data?.data)) {
        this.stepFormForceRules = true
        return
      }
      this.stepFormForceRules = false

      delete data.data['id']

      if (data?.data?.link) {
        data.data.link = this.addHttpsProtocol(data.data.link)
      }

      if (stepId) {
        // update
        await this.updateCourseStep({
          ...data,
          stepId,
        })
      } else {
        // create
        await this.createCourseStep(data)
      }

      this.$refs?.['courseStepFormDialog']?.cancel?.()
      this.courseStepFormDialogActive = false
      this.$store.commit('coursesStates/setCurrentCourseStep', null)
      await this.getCourseSteps({ courseId: data.courseId })
    },
    showConfirmDeleteStepDialog(step) {
      this.$store.commit('coursesStates/setCurrentCourseStep', step)
      this.confirmDeleteStepDialogAlert = true
    },
    confirmDeleteStepDialogCancel() {
      this.confirmDeleteStepDialogAlert = false
      this.$store.commit('coursesStates/setCurrentCourseStep', null)
    },
    async confirmDeleteStepDialogSubmit() {
      await this.deleteCourseStep({
        courseId: this.$route.params.id,
        stepId: this.currentCourseStep.id,
      })
      this.confirmDeleteStepDialogAlert = false
      this.$store.commit('coursesStates/setCurrentCourseStep', null)
      await this.getCourseSteps({ courseId: this.$route.params.id })
    },
    showConfirmDeleteCourseDialog() {
      this.confirmDeleteCourseDialogAlert = true
    },
    confirmDeleteCourseDialogCancel() {
      this.confirmDeleteCourseDialogAlert = false
    },
    async confirmDeleteCourseDialogSubmit() {
      await this.deleteCourseDefinition({
        id: this.$route.params.id,
      })
      this.confirmDeleteCourseDialogAlert = false

      this.$router.push({
        name: 'Courses',
        path: '/courses',
      })
    },
    stepDragged(props) {
      if (props?.moved) {
        const { element, newIndex, oldIndex } = props.moved
        if (oldIndex === newIndex) return
        const { nextStep, prevStep } = props

        this.moveCourseStep({
          courseId: this.$route.params.id,
          stepId: element.id,
          prevStep,
          nextStep,
        })
      }
    },
    openDelete(step) {
      this.showConfirmDeleteStepDialog(step)
    },
    openEdit(step) {
      this.openEditStep(step)
    },
    async applyAssignParticipants() {
      try {
        const {
          allSelected = false,
          allAndFutureSelected = false,
          selectedParticipants = [],
        } = this.$refs['participantSelector']

        const selectedParticipantsIds = selectedParticipants
          .map(el => el?.id || el?.participantId || null)
          .filter(el => el !== null)

        const response = await this.assignCourseDefinition({
          id: this.$route.params.id,
          all: allSelected,
          autoAssignAll: allAndFutureSelected,
          participants:
            allSelected || allAndFutureSelected
              ? []
              : [...selectedParticipantsIds],
        })

        if (response === true) {
          // success response
          this.closeParticipantSelectorDialog()
        }
      } catch (error) {
        // do nothing
      }
    },
    openParticipantSelectorDialog() {
      this.getCourseDefinitionParticipants({ id: this.$route.params.id })
      this.participantSelectorDialogActive = true
      this.setLoadingCurrentCourse(false)
    },
    closeParticipantSelectorDialog() {
      this.$refs?.['participantSelectorDialog']?.cancel?.()
    },
    afterCloseParticipantSelectorDialog() {
      this.initialSelectAll = false
      this.initialSelectAllAndFuture = false
      this.initialSelection = []
      this.participantSelectorDialogActive = false
      this.$store.commit('coursesStates/setCurrentCourseParticipants', [])
    },
    navigateToCreateCourse() {
      this.$router.push({
        name: 'Create Course',
        path: '/courses/add',
      })
    },
  },
  watch: {
    async currentCourse(newValue) {
      this.setBreadcrumbCurrentView(newValue?.title || '')
    },
    $route(to, from) {
      // get new current patient data when patient id changed
      if (
        to.params?.id !== undefined &&
        from.params?.id !== to.params?.id &&
        to?.params?.id
      ) {
        this.getCourseData(to.params.id)
      }
    },
    participantSelectorDialogActive(newVal, oldVal) {},
    loadingCurrentCourseParticipants(val) {
      if (!val) {
        this.initialSelection = [...(this.currentCourseParticipants || [])]
        this.initialSelectAll = this.currentCourseAssignmentStatus === 'ALL'
        this.initialSelectAllAndFuture =
          this.currentCourseAssignmentStatus === 'ALL'
        this.$refs?.['participantSelector']?.init?.()
      }
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.course-details {
  padding: (2 * $gutter) (2 * $gutter) (2.5 * $gutter);
  height: 100%;

  &__fab {
    bottom: 0;
    right: 0;
    position: fixed;
    margin: 0 16px 16px 0;
  }
}
</style>
